import { format, parseISO } from 'date-fns'
import { sum } from 'lodash-es'
import {
  Analysis,
  ChatSession,
  Message,
  OrgSnapshot,
  ServerAnalysis,
  ServerChatSession,
  ServerMessage,
  ServerOrgSnapshot,
} from './types'

// these functions transform data from the server into the shape the frontend expects.

export const transformAnalysis = (serverAnalysis: ServerAnalysis): Analysis => {
  const projectedState = serverAnalysis.attributes.projected_state
  const state = projectedState.state
  const org = state.org

  return {
    id: serverAnalysis.id,
    messageId: serverAnalysis.relationships.message.data.id,
    numRoles: org.roles_count,
    orgSize: org.members_count,
    name: serverAnalysis.attributes.name,
    totalCost: org.total_costs_usd,
    avgSalary: org.avg_salary_usd,
    teams: state.teams.map((team) => {
      const openingCounts = sum(
        team.positions?.map((p) => p.opening_count ?? 0) ?? []
      )
      return {
        id: team.id,
        name: team.name,
        positions: team.positions,
        numMembers:
          state.users.filter((user) => user.team_id === team.id).length +
          openingCounts,
      }
    }),
    users: state.users,
    skills: state.skills,
    changes: projectedState.applied_diff.changes,
  }
}

export const transformChat = (chat: ServerChatSession): ChatSession => ({
  description:
    chat.attributes.name ??
    format(parseISO(chat.attributes.created_at), 'MMM do yyyy, h:mma'),
  id: Number(chat.id),
  orgSnapshotId: Number(chat.attributes.org_snapshot_id),
  starred: chat.attributes.is_starred,
})

export const transformMessage = (message: ServerMessage): Message => ({
  id: message.id,
  role: message.attributes.role,
  content: message.attributes.content,
})

export const transformOrgSnapshot = (data: ServerOrgSnapshot): OrgSnapshot => {
  const snapshot = data.attributes.snapshot_data
  const org = snapshot.org
  const transformed = {
    id: data.id,
    orgSize: org.members_count,
    totalCost: org.total_costs_usd,
    avgSalary: org.avg_salary_usd,
    numRoles: org.roles_count,
    positionsById: new Map(),
    teams: snapshot.teams.map((team) => ({
      id: team.id,
      name: team.name,
      positions: team.positions,
      numMembers: snapshot.users.filter((user) => user.team_id === team.id)
        .length,
    })),
    teamsById: new Map(),
    timestamp: new Date(data.attributes.created_at),
    users: snapshot.users,
    usersById: new Map(),
    skills: snapshot.skills,
    skipped: {
      users: snapshot.users_skipped,
      positions: snapshot.positions_skipped,
    },
  }

  transformed.teams.forEach((team) => {
    transformed.teamsById.set(team.id, team)
    team.positions.forEach((position) => {
      transformed.positionsById.set(position.id, position)
    })
  })

  transformed.users.forEach((user) => {
    transformed.usersById.set(user.id, user)
  })

  return transformed
}
